import { Box, Grid } from "@mui/material";
import {
  clearAllFilters,
  setFiltersApplied,
  setSelectedFilters,
  setSelectedFunctionalAreas,
  setSelectedPlanType
} from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import MultipleSelectCheckmarks from "components/common/dropdowns/MultiSelect";
import { intersection } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { brandDeadlinesType, initiativePlansType, planTypes } from "utils/CommonConstants";
import {
  functionalAreaIdsToNames,
  functionalAreaNamesToIds,
  getListOfFilters,
  planTypeIdToName
} from "utils/CommonFunctions";
import ButtonLink from "../Link";
import SearchAppBar from "../searchBar";
import FilterCategories, { TYPES } from "./FilterCategories";

interface Props {
  functionalAreaId?: string;
  isSearchBarVisible?: boolean;
  isFilterCategoriesVisible?: boolean;
  suggested?: boolean;
  getInputDetails?: (prop: string) => void;
  isPlanTypeVisible?: boolean;
  value?: string;
}

function FilterButtons(props: Props) {
  const dispatch = useAppDispatch();

  const {
    functionalAreaId,
    getInputDetails,
    isSearchBarVisible,
    isFilterCategoriesVisible = true,
    suggested = false,
    isPlanTypeVisible = true,
    value = ""
  } = props;
  const functionalAreas = useAppSelector((state: any) => state.functionalAreas?.list);
  const selectedPlanType = useAppSelector((state: any) => state.app.selectedPlanType);
  const [openDrawer, setOpenDrawer] = useState(false);
  const filters = useAppSelector((state: any) => state.app.filters);
  const [filtersList, setFiltersList] = useState(filters);
  const { isMobileScreen } = useContext(ThemeContext);
  const { selectedFunctionalAreas, filtersApplied } = useAppSelector((state: any) => state.app);

  const getPlanTypesList = () => {
    let planTypesArray = [...planTypes];

    if (!suggested) {
      planTypesArray = planTypesArray?.filter(
        (item: any) => item.planTypeId === brandDeadlinesType || item.planTypeId === initiativePlansType
      );
    }

    return planTypesArray?.map((item: any) => {
      return { id: item.planTypeId, name: item.planTypeName };
    });
  };

  useEffect(() => {
    if (functionalAreaId) {
      const selectedFilters = filters.filter((item: any) => item.functionalAreaIds?.indexOf(functionalAreaId) > -1);
      setFiltersList(selectedFilters);
    } else {
      setFiltersList(filters);
    }
  }, [functionalAreaId, filters]);

  useEffect(() => {
    if (selectedFunctionalAreas.length) {
      const selectedFilters = filters.filter(
        (item: any) => intersection(item.functionalAreaIds, selectedFunctionalAreas).length > 0
      );
      setFiltersList(selectedFilters);
    }
  }, [selectedFunctionalAreas, filters]);

  const onMultiSelectChange = (value: any, name: any) => {
    if (value.length > 0) {
      dispatch(setFiltersApplied(true));
    } else if (getListOfFilters().length === 1) dispatch(setFiltersApplied(false));

    if (name === "Plan Type") {
      let result = value?.map(
        (item: any) =>
          planTypes.find((type: any) => {
            return type.planTypeName === item;
          })?.planTypeId
      );

      dispatch(setSelectedPlanType(result));
      return;
    }

    const areaDetails = functionalAreaNamesToIds(value);
    dispatch(setSelectedFunctionalAreas(areaDetails));
    dispatch(setSelectedFilters([]));
    setFiltersList(filters);
  };

  return (
    <>
      <Grid container marginBottom={"16px"}>
        <Grid item md={4} xs={12}>
          <Box
            sx={{
              width: { xs: "100%", md: "300px" }
            }}
          >
            {isSearchBarVisible && (
              <SearchAppBar value={value} getInputDetails={getInputDetails} divider></SearchAppBar>
            )}
          </Box>
        </Grid>
        <Grid
          sx={{
            marginTop: { xs: "16px", md: "0px" },
            marginBottom: { xs: "16px", md: "0px" }
          }}
          item
          md={8}
          xs={12}
          display="flex"
          alignItems={"center"}
          flexWrap="wrap"
          justifyContent={isMobileScreen ? "space-between" : "end"}
          gap="16px"
        >
          {filtersApplied && (
            <Grid item xs={12} sm={1}>
              <ButtonLink
                text="Clear all"
                sx={{ color: tokens.Text.General.Color }}
                textVariant="body2"
                underline="none"
                onClick={() => {
                  dispatch(clearAllFilters());
                }}
              />
            </Grid>
          )}
          {isPlanTypeVisible && (
            <MultipleSelectCheckmarks
              minWidth={isMobileScreen ? "140px" : "180px"}
              name="Plan Type"
              label="Plan Type"
              options={getPlanTypesList()}
              initialValues={planTypeIdToName(selectedPlanType)}
              onChange={onMultiSelectChange}
            ></MultipleSelectCheckmarks>
          )}

          {!functionalAreaId && (
            <MultipleSelectCheckmarks
              minWidth={isMobileScreen ? "140px" : "180px"}
              name="Functional Area"
              label="Functional Area"
              options={functionalAreas}
              initialValues={functionalAreaIdsToNames(selectedFunctionalAreas)}
              onChange={onMultiSelectChange}
            ></MultipleSelectCheckmarks>
          )}
          {isFilterCategoriesVisible && (
            <Box marginLeft={isMobileScreen ? "0px" : "4px"}>
              {
                <FilterCategories
                  openDrawer={openDrawer}
                  setOpenDrawer={setOpenDrawer}
                  filters={filtersList}
                  type={TYPES.Drawer}
                />
              }
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
}

export default FilterButtons;
